import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useRef } from "react";

import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { PolicyEvaluationRecord } from "types/generated";
import { uniqByKey } from "utils/uniq";

import { ENTITIES_PER_PAGE, INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "./constants";
import { SEARCH_POLICIES_SAMPLES } from "./gql";

const useSearchPolicySample = (id: string) => {
  const { onError } = useTypedContext(FlashContext);
  const cachedEdges = useRef<PolicyEvaluationRecord[]>([]);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [urlParams]);

  const { error, loading, data, previousData, fetchMore } = useQuery(SEARCH_POLICIES_SAMPLES, {
    variables: {
      id,
      input: {
        predicates,
        fullTextSearch: searchInput,
        first: ENTITIES_PER_PAGE,
        after: null,
        requestedPage: null,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    nextFetchPolicy: "cache-first",
  });

  // FYI: policy is defined base on type but BE sends empty object sometimes
  const rawSearchTemplates = useMemo(() => {
    const dataPolicies =
      data?.policy?.searchEvaluationRecords || previousData?.policy?.searchEvaluationRecords;

    return dataPolicies;
  }, [data?.policy?.searchEvaluationRecords, previousData?.policy?.searchEvaluationRecords]);

  const memoizedEntities = useMemo(() => {
    const sourceEdges = rawSearchTemplates?.edges.map((edge) => edge.node) || [];
    const edges = loading && !sourceEdges.length ? cachedEdges.current : sourceEdges;

    if (!loading) {
      cachedEdges.current = sourceEdges;
    }

    return edges;
  }, [rawSearchTemplates?.edges, loading]);

  const isItemLoaded = useCallback(
    (value: number) => value < memoizedEntities.length,
    [memoizedEntities.length]
  );

  const loadMoreItems = useCallback(async () => {
    try {
      if (
        data?.policy?.searchEvaluationRecords?.pageInfo.endCursor &&
        data?.policy?.searchEvaluationRecords?.pageInfo.hasNextPage
      ) {
        await fetchMore({
          updateQuery: (prev, { fetchMoreResult }) => {
            const previousData = prev?.policy?.searchEvaluationRecords ? prev : data;

            if (!fetchMoreResult || !fetchMoreResult?.policy.searchEvaluationRecords)
              return previousData;

            const result = fetchMoreResult?.policy.searchEvaluationRecords;

            if (!result || !result?.edges?.length) return previousData;

            const edges = uniqByKey(
              [...(previousData?.policy.searchEvaluationRecords?.edges || []), ...result.edges],
              "cursor"
            );

            return {
              policy: {
                ...previousData?.policy,
                searchEvaluationRecords: {
                  ...result,
                  edges,
                },
              },
            };
          },
          variables: {
            input: {
              first: ENTITIES_PER_PAGE,
              after: data.policy.searchEvaluationRecords.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              requestedPage: null,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  }, [data, fetchMore, searchInput, predicates, sortOptionFields, onError]);

  return {
    isItemLoaded,
    loadMoreItems,
    entities: memoizedEntities,
    error,
    loading,
    isPageEmpty: !!(data && !memoizedEntities.length && !searchInput && predicates.length === 0),
    hasNoFilteringResults:
      !!data && !memoizedEntities.length && (!!searchInput || predicates.length > 0),
  };
};

export default useSearchPolicySample;
